import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import classNames from 'classnames'
import { uniqueId } from 'lodash'
import { useCallback, useRef, useState } from 'react'
import { type MenuOption } from '@opallabs/concepts'
import { SingleSelect, TextInput, TextInputType } from '@opallabs/lapidary'
import * as styles from './styles'
import {
  type CallbackData,
  type MultiInputProps,
  type RequiredKeysAndOptionalValues,
} from './types'

export { type MultiInputProps }

/**
 * Combination of a number input and a dropdown, representing a count of the selected option — for example, ‘30 days’
 * or ‘50 display ads’.
 *
 * It’s nonsensical to have ‘NaN [option]s’ (e.g. `null` days) or ‘[number] [null]’ (50… nothings?). The component is
 * also used in some ‘modeless’ forms (forms where changing a control instantly changes data without requiring the user
 * to explicitly save the change). These considerations result in the component having a few special behaviors:
 * - The `onChange` callback passes `null` unless both controls are valid. If the user clears either field we assume
 *   they want to unset the data the component represents.
 * - The dropdown is disabled if the input is empty (except when `required`). We assume that if the user sets a value
 *   they want to set the data the component represents.
 *
 * @note `options` can be updated on the fly. You can use this to change the pluralization of the option `label`s. This
 * is why the props are `defaultOptionValue` and `initialOptionValue` instead of `defaultOption` and `initialOption` — if
 * these props took a full object they would fall out of sync when `options` is modified.
 */
export const MultiInput = <Value extends string | number | boolean = string>({
  className,
  defaultOptionValue,
  fieldsetName,
  hideFieldsetLegend = false,
  htmlId,
  initialOptionValue,
  initialQuantity,
  maxValue,
  minValue = 0,
  onChange,
  options,
  required = false,
}: MultiInputProps<Value>) => {
  const id = useRef(htmlId ?? uniqueId(styles.classes.container))
  // This tracks the `value` instead of the entire object so the updating `label`s in `options` doesn't affect the
  // selection state.
  const [optionValue, setOptionValue] = useState<Value | undefined>(
    initialOptionValue ?? defaultOptionValue
  )
  const findOption = (
    targetValue: Value | undefined
  ): MenuOption<Value> | undefined => {
    if (targetValue === undefined) return
    return options.find(
      ({ value: candidateValue }) => candidateValue === targetValue
    )
  }
  const selectedOption = findOption(optionValue)

  const legend = fieldsetName ? (
    <legend className={styles.classes.legend}>{fieldsetName}</legend>
  ) : null

  const wrappedOnChange = useCallback(
    ({
      option,
      quantity,
    }: RequiredKeysAndOptionalValues<CallbackData<Value>>) => {
      if (option && quantity !== undefined) onChange({ option, quantity })
      else onChange(null)
    },
    [onChange]
  )

  const [value, setValue] = useState(initialQuantity)
  const updateValue = (rawValue: string) => {
    const asNumber = parseInt(rawValue, 10)
    const newQuantity = Number.isNaN(asNumber) ? undefined : asNumber
    setValue(newQuantity)

    const option = findOption(optionValue)
    wrappedOnChange({ option, quantity: newQuantity })
  }

  const updateOption = (option: MenuOption<Value> | null) => {
    const newOptionValue = option?.value
    setOptionValue(newOptionValue)

    wrappedOnChange({ option: option ?? undefined, quantity: value })
  }

  return (
    <fieldset
      className={classNames(styles.classes.container, className)}
      css={styles.container}
      id={id.current}
    >
      {hideFieldsetLegend ? <VisuallyHidden>{legend}</VisuallyHidden> : legend}

      <TextInput
        aria-label="Quantity"
        className={styles.classes.value}
        initialValue={value?.toString()}
        maxValue={maxValue}
        minValue={minValue}
        onChange={updateValue}
        required={required}
        type={TextInputType.Number}
        validator={
          required
            ? (rawValue) =>
                rawValue.trim() === ''
                  ? { errorMessage: 'Required', valid: false }
                  : { valid: true }
            : undefined
        }
      />

      {/* Has to be `ControlledRoot` so updates to `options` update the list. Used by `<DurationControl>`. */}
      <SingleSelect.ControlledRoot
        onChange={updateOption}
        options={options}
        selectedOption={selectedOption ?? null}
      >
        <SingleSelect.Body className={styles.classes.options}>
          <SingleSelect.Trigger disabled={!required && value === undefined}>
            <SingleSelect.TriggerText />
          </SingleSelect.Trigger>

          <SingleSelect.Menu>
            <SingleSelect.MenuItems />
          </SingleSelect.Menu>
        </SingleSelect.Body>
      </SingleSelect.ControlledRoot>
    </fieldset>
  )
}
MultiInput.displayName = 'Satellite: MultiInput'
